import React, { useEffect, useState } from 'react';
import { sendFeedback } from '../utils/Api';

const initialFormState = {
  siteRating: '',
  dishSearchSpeed: '',
  siteImprovements: '',
  dishesRating: '',
  favoriteDish: '',
  dishesImprovements: '',
  recommendation: ''
};

const hasAnyFeedbackValue = (formData) =>
  Object.values(formData).some((value) => {
    if (typeof value !== 'string') {
      return Boolean(value);
    }

    return value.trim() !== '';
  });

function PopupFeedback({ isOpen, onClose }) {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormState);
      setIsSubmitting(false);
      setIsSubmitted(false);
      setErrorMessage('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleTextChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleOptionChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!hasAnyFeedbackValue(formData)) {
      setErrorMessage('');
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await sendFeedback(formData);
      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Не удалось отправить отзыв. Попробуйте еще раз позже.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderChoiceButton = (name, value, label) => (
    <button
      type="button"
      className={`feedback-popup__choice ${formData[name] === value ? 'feedback-popup__choice_active' : ''}`}
      onClick={() => handleOptionChange(name, value)}
    >
      {label}
    </button>
  );

  return (
    <div className="popup__overlay" onClick={onClose}>
      <div className="feedback-popup" onClick={(event) => event.stopPropagation()}>
        <button className="feedback-popup__close-button" type="button" onClick={onClose} aria-label="Закрыть форму отзыва"></button>

        {isSubmitted ? (
          <div className="feedback-popup__success">
            <h2 className="feedback-popup__title">Спасибо за отзыв ❤️</h2>
            <p className="feedback-popup__subtitle">Мы правда читаем каждый отзыв и используем его, чтобы стать лучше.</p>
            <button className="feedback-popup__submit" type="button" onClick={onClose}>
              Закрыть
            </button>
          </div>
        ) : (
          <>
            <p className="feedback-popup__eyebrow">Нам важно ваше мнение</p>
            <h2 className="feedback-popup__title">Помогите нам стать лучше — это займет меньше минуты</h2>

            <form className="feedback-popup__form" onSubmit={handleSubmit}>
              <div className="feedback-popup__section">
                <h3 className="feedback-popup__section-title">🧭 Удобство сайта</h3>

                <fieldset className="feedback-popup__fieldset">
                  <legend className="feedback-popup__legend">Насколько вам было удобно пользоваться сайтом?</legend>
                  <div className="feedback-popup__rating-row">
                    {['1', '2', '3', '4', '5'].map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`feedback-popup__rating ${formData.siteRating === value ? 'feedback-popup__rating_active' : ''}`}
                        onClick={() => handleOptionChange('siteRating', value)}
                      >
                        {value} ⭐
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="feedback-popup__fieldset">
                  <legend className="feedback-popup__legend">Удалось ли вам быстро найти нужное блюдо?</legend>
                  <div className="feedback-popup__choices">
                    {renderChoiceButton('dishSearchSpeed', 'Да', 'Да')}
                    {renderChoiceButton('dishSearchSpeed', 'Скорее да', 'Скорее да')}
                    {renderChoiceButton('dishSearchSpeed', 'Скорее нет', 'Скорее нет')}
                    {renderChoiceButton('dishSearchSpeed', 'Нет', 'Нет')}
                  </div>
                </fieldset>

                <label className="feedback-popup__label">
                  <input
                    className="feedback-popup__input"
                    type="text"
                    name="siteImprovements"
                    value={formData.siteImprovements}
                    onChange={handleTextChange}
                    placeholder="Что можно улучшить на сайте?"
                  />
                </label>
              </div>

              <div className="feedback-popup__section">
                <h3 className="feedback-popup__section-title">🍕 Отзыв о блюдах</h3>

                <fieldset className="feedback-popup__fieldset">
                  <legend className="feedback-popup__legend">Если вы уже попробовали блюда, будем рады вашему отзыву. Поделиться впечатлениями можно в любое время через кнопку «Обратная связь» в верхнем меню или внизу сайта.</legend>
                  <div className="feedback-popup__choices">
                    {renderChoiceButton('dishesRating', 'Очень вкусно 😍', 'Очень вкусно 😍')}
                    {renderChoiceButton('dishesRating', 'Нормально 🙂', 'Нормально 🙂')}
                    {renderChoiceButton('dishesRating', 'Есть замечания 😐', 'Есть замечания 😐')}
                  </div>
                </fieldset>

                <label className="feedback-popup__label">
                  <textarea
                    className="feedback-popup__textarea"
                    name="favoriteDish"
                    value={formData.favoriteDish}
                    onChange={handleTextChange}
                    placeholder="Что именно вам понравилось больше всего?"
                  />
                </label>

                <label className="feedback-popup__label">
                  <textarea
                    className="feedback-popup__textarea"
                    name="dishesImprovements"
                    value={formData.dishesImprovements}
                    onChange={handleTextChange}
                    placeholder="Что можно улучшить?"
                  />
                </label>
              </div>

              <div className="feedback-popup__section">
                <h3 className="feedback-popup__section-title">🚀 Завершение</h3>

                <fieldset className="feedback-popup__fieldset">
                  <legend className="feedback-popup__legend">Порекомендуете ли вы нас друзьям?</legend>
                  <div className="feedback-popup__choices">
                    {renderChoiceButton('recommendation', 'Да', 'Да')}
                    {renderChoiceButton('recommendation', 'Не уверен(а)', 'Не уверен(а)')}
                    {renderChoiceButton('recommendation', 'Нет', 'Нет')}
                  </div>
                </fieldset>

                <p className="feedback-popup__note">Спасибо, что помогаете нам становиться лучше</p>
                <p className="feedback-popup__note feedback-popup__note_muted">Мы правда читаем каждый отзыв</p>
              </div>

              {errorMessage && <p className="feedback-popup__error">{errorMessage}</p>}

              <button
                className="feedback-popup__submit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Отправляем...' : 'Отправить отзыв'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default PopupFeedback;
